import { useEffect, useState } from 'react';
import { Slider } from '@/components/ui/slider';
import type { PriceBucket, PriceStats } from '../../-types';

interface PriceRangeFilterProps {
  priceStats: PriceStats;
  priceHistogram?: PriceBucket[];
  currentMin?: number;
  currentMax?: number;
  onCommit: (min: number | undefined, max: number | undefined) => void;
}

export function PriceRangeFilter({
  priceStats,
  priceHistogram,
  currentMin,
  currentMax,
  onCommit,
}: PriceRangeFilterProps) {
  const [localRange, setLocalRange] = useState([
    currentMin ?? priceStats.min,
    currentMax ?? priceStats.max,
  ]);

  useEffect(() => {
    setLocalRange([currentMin ?? priceStats.min, currentMax ?? priceStats.max]);
  }, [currentMin, currentMax, priceStats]);

  const formatPrice = (price: number) => new Intl.NumberFormat('he-IL').format(price);

  const handleValueCommit = (values: number[]) => {
    const [min, max] = values;
    onCommit(min === priceStats.min ? undefined : min, max === priceStats.max ? undefined : max);
  };

  // Calculate histogram visualization
  const maxCount =
    priceHistogram && priceHistogram.length > 0
      ? Math.max(...priceHistogram.map((b) => b.count))
      : 0;

  return (
    <div className="space-y-3 px-2 py-2">
      {/* Price display */}
      <div className="flex justify-between items-center px-2 py-2 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-700/60">
        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
          {formatPrice(localRange[0])} ₪
        </span>
        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">עד</span>
        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
          {formatPrice(localRange[1])} ₪
        </span>
      </div>

      {/* Histogram */}
      {priceHistogram && priceHistogram.length > 0 && maxCount > 0 && (
        <div className="px-2 py-2 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-700/50 border border-slate-200 dark:border-slate-600/50 histogram-container">
          <div className="flex items-end justify-center gap-px h-20 w-full mb-0">
            {priceHistogram.map((bucket) => (
              <div
                key={`bucket-${bucket.min}-${bucket.max}`}
                className="flex-1 h-full flex items-end justify-center"
              >
                <div
                  className="w-full rounded-sm transition-all duration-300 hover:shadow-md hover:shadow-blue-500/20 histogram-bar"
                  style={{ height: `${(bucket.count / maxCount) * 100}%` }}
                  title={`${bucket.count} מוצרים בטווח שבין ₪${formatPrice(bucket.min)} ל ₪${formatPrice(bucket.max)}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Slider */}
      <div className="px-2 py-3 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-700/50 border border-slate-200 dark:border-slate-600/50 slider-container">
        <Slider
          dir="rtl"
          value={localRange}
          onValueChange={setLocalRange}
          onValueCommit={handleValueCommit}
          min={priceStats.min}
          max={priceStats.max}
          step={1}
          className="w-full"
        />
      </div>

      {/* Min/Max labels */}
      <div className="flex justify-between items-center text-xs text-slate-600 dark:text-slate-400 font-medium px-2">
        <span>{formatPrice(priceStats.min)} ₪</span>
        <span>{formatPrice(priceStats.max)} ₪</span>
      </div>
    </div>
  );
}
